 
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { getIncomeStatement } from '@store/Finance/FinancialReports/FinancialReportsSlice';
import Breadcrumb from '@components/Shared/Breadcrumb';
import { Card } from '@components/card/card';
import { useTranslation } from 'react-i18next';
import Loading from '@components/Shared/Loader/Loading';
import { Spinner, Alert } from 'react-bootstrap';

const IncomeStatement = () => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const [filter, setFilter] = useState({
    startDate: '',
    endDate: '',
    accountlevel: '1'
  });

  const { IncomeStatement: data, loading, error } = useAppSelector(s => s.FinancialReports);
 
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = () => {
    dispatch(
      getIncomeStatement({
        startDate: filter.startDate,
        endDate: filter.endDate,
        accountlevel: filter.accountlevel
      })
    );
  };

  const resetFilters = () => {
    setFilter({
      startDate: '',
      endDate: '',
      accountlevel: '1'
    });
  };

  const formatCurrency = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // استخراج البيانات الرئيسية
  const revenues = data?.list?.find(item => item.accountNumber === '40000000');
  const expenses = data?.list?.find(item => item.accountNumber === '50000000');
  const netIncome = data?.list?.find(item => item.accountNumber === '00000000');

  // حساب الإجماليات
  const calculateTotals = () => {
    const totalRevenue = revenues?.amounT_CR || 0;
    const totalExpenses = expenses?.amounT_DR || 0;
    const netProfit = totalRevenue - totalExpenses;

    return { totalRevenue, totalExpenses, netProfit };
  };

  const { totalRevenue, totalExpenses, netProfit } = calculateTotals();

  const BcrumbList = [
    {
      name: 'Home',
      link: ':moduleHome'
    },
    {
      name: 'قائمة الدخـــل',
      link: null
    }
  ];

  return (
    <>
     

      <Card className='mb-4'>
        <div className='card-header d-flex justify-content-between align-items-center'>
          <h6 className='mb-0'>
            <i className='fas fa-chart-line me-2'></i>
            قائمة الدخل
            <span className='badge bg-primary ms-2'>التقرير المالي</span>
          </h6>
          <div>
            <span className='badge bg-success me-2'>الإيرادات: {formatCurrency(totalRevenue)}</span>
            <span className='badge bg-danger me-2'>المصروفات: {formatCurrency(totalExpenses)}</span>
            <span className={`badge ${netProfit >= 0 ? 'bg-success' : 'bg-danger'}`}>
              صافي الدخل: {formatCurrency(Math.abs(netProfit))}
            </span>
          </div>
        </div>

        <div className='card-body p-0'>
          {/* قسم الفلاتر */}
          <div className='p-3 border-bottom'>
            <div className='row mb-3'>
              <div className='col-lg-3 mb-2'>
                <div className='input-group flex-nowrap'>
                  <span className='input-group-text fw-bold'>من تاريـخ</span>
                  <input
                    type='date'
                    autoComplete='off'
                    className='form-control'
                    value={filter.startDate}
                    onChange={e => {
                      setFilter({ ...filter, startDate: e.target.value });
                    }}
                  />
                </div>
              </div>

              <div className='col-lg-3 mb-2'>
                <div className='input-group flex-nowrap'>
                  <span className='input-group-text fw-bold'>إلى تاريـخ</span>
                  <input
                    type='date'
                    autoComplete='off'
                    className='form-control'
                    value={filter.endDate}
                    onChange={e => {
                      setFilter({ ...filter, endDate: e.target.value });
                    }}
                  />
                </div>
              </div>

              <div className='col-lg-3 mb-2'>
                <div className='input-group flex-nowrap'>
                  <span className='input-group-text fw-bold'>مستوى الحساب</span>
                  <select
                    className='form-select'
                    value={filter.accountlevel}
                    onChange={e => {
                      setFilter({ ...filter, accountlevel: e.target.value });
                    }}
                  >
                    <option value='1'>المستوى 1</option>
                    <option value='2'>المستوى 2</option>
                    <option value='3'>المستوى 3</option>
                    <option value='4'>المستوى 4</option>
                    <option value='5'>المستوى 5</option>
                    <option value='6'>المستوى 6</option>
                  </select>
                </div>
              </div>

              <div className='col-lg-3 mb-2 d-flex gap-2'>
                <button className='btn btn-secondary flex-fill' onClick={resetFilters}>
                  إعادة تعيين
                </button>
                <button className='btn btn-theme flex-fill' onClick={fetchData} disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner
                        as='span'
                        animation='border'
                        size='sm'
                        role='status'
                        aria-hidden='true'
                        className='me-2'
                      />
                      جاري التحميل...
                    </>
                  ) : (
                    'تحميل التقرير'
                  )}
                </button>
              </div>
            </div>

            {error && (
              <Alert variant='danger' className='mt-3 mb-0'>
                خطأ في تحميل البيانات: {error}
              </Alert>
            )}
          </div>

          {/* قسم النتائج */}
          <Loading loading={loading} error={error} Type='Dots'>
            <div className='table-responsive'>
              <table className='table table-hover mb-0'>
                <thead className='table-actived fw-bold'>
                  <tr>
                    <th className='text-center small'>كود الحساب</th>
                    <th className='text-center small'>اسم الحســاب</th>
                    <th className='text-center small'>المدين (DR)</th>
                    <th className='text-center small'>الدائن (CR)</th>
                  </tr>
                </thead>
                <tbody>
                  {/* قسم الإيرادات */}
                  <tr className='table-info'>
                    <td colSpan={4} className='text-center fw-bold fs-6 py-2'>
                      <i className='fas fa-arrow-up me-2'></i>
                      الإيرادات
                    </td>
                  </tr>

                  {revenues?.children?.map((item, index) => (
                    <tr key={item.id || index}>
                      <td className='text-center align-middle'>
                        <strong>{item.accountNumber}</strong>
                      </td>
                      <td className='align-middle'>{i18n.language === 'ar' ? item.name2 : item.name}</td>
                      <td className='text-center align-middle'>
                        <span className='badge bg-danger fs-6'>{formatCurrency(item.amounT_DR)}</span>
                      </td>
                      <td className='text-center align-middle'>
                        <span className='badge bg-success fs-6'>{formatCurrency(item.amounT_CR)}</span>
                      </td>
                    </tr>
                  ))}

                  <tr className='table-success fw-bold'>
                    <td colSpan={2} className='text-center align-middle'>
                      إجمالي الإيرادات
                    </td>
                    <td className='text-center align-middle'>
                      <span className='badge bg-danger fs-5'>{formatCurrency(revenues?.amounT_DR)}</span>
                    </td>
                    <td className='text-center align-middle'>
                      <span className='badge bg-success fs-5'>{formatCurrency(revenues?.amounT_CR)}</span>
                    </td>
                  </tr>

                  {/* فاصل */}
                  <tr>
                    <td colSpan={4} className='p-0'>
                      <div className='border-top my-2'></div>
                    </td>
                  </tr>

                  {/* قسم المصروفات */}
                  <tr className='table-warning'>
                    <td colSpan={4} className='text-center fw-bold fs-6 py-2'>
                      <i className='fas fa-arrow-down me-2'></i>
                      المصروفات
                    </td>
                  </tr>

                  {expenses?.children?.map((item, index) => (
                    <tr key={item.id || index}>
                      <td className='text-center align-middle'>
                        <strong>{item.accountNumber}</strong>
                      </td>
                      <td className='align-middle'>{i18n.language === 'ar' ? item.name2 : item.name}</td>
                      <td className='text-center align-middle'>
                        <span className='badge bg-danger fs-6'>{formatCurrency(item.amounT_DR)}</span>
                      </td>
                      <td className='text-center align-middle'>
                        <span className='badge bg-success fs-6'>{formatCurrency(item.amounT_CR)}</span>
                      </td>
                    </tr>
                  ))}

                  <tr className='table-danger fw-bold'>
                    <td colSpan={2} className='text-center align-middle'>
                      إجمالي المصروفات
                    </td>
                    <td className='text-center align-middle'>
                      <span className='badge bg-danger fs-5'>{formatCurrency(expenses?.amounT_DR)}</span>
                    </td>
                    <td className='text-center align-middle'>
                      <span className='badge bg-success fs-5'>{formatCurrency(expenses?.amounT_CR)}</span>
                    </td>
                  </tr>

                  {/* فاصل */}
                  <tr>
                    <td colSpan={4} className='p-0'>
                      <div className='border-top my-2'></div>
                    </td>
                  </tr>

                  {/* صافي الدخل */}
                  <tr className={`fw-bold ${netProfit >= 0 ? 'table-success' : 'table-danger'}`}>
                    <td colSpan={2} className='text-center align-middle fs-5'>
                      {netIncome
                        ? i18n.language === 'ar'
                          ? netIncome.name2
                          : netIncome.name
                        : netProfit >= 0
                          ? 'صافي الربح'
                          : 'صافي الخسارة'}
                    </td>
                    <td className='text-center align-middle'>
                      {netProfit < 0 && (
                        <span className='badge bg-danger fs-5'>{formatCurrency(Math.abs(netProfit))}</span>
                      )}
                    </td>
                    <td className='text-center align-middle'>
                      {netProfit >= 0 && <span className='badge bg-success fs-5'>{formatCurrency(netProfit)}</span>}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Loading>
        </div>

        <div className='card-footer d-flex justify-content-between align-items-center'>
          <div className='text-muted small'>
            فترة التقرير: {filter.startDate || 'بداية'} - {filter.endDate || 'نهاية'}
          </div>
          <div className='d-flex gap-3'>
            <span className='text-success fw-bold'>إجمالي الإيرادات: {formatCurrency(totalRevenue)}</span>
            <span className='text-danger fw-bold'>إجمالي المصروفات: {formatCurrency(totalExpenses)}</span>
            <span className={`fw-bold ${netProfit >= 0 ? 'text-success' : 'text-danger'}`}>
              {netProfit >= 0 ? 'صافي الربح: ' : 'صافي الخسارة: '}
              {formatCurrency(Math.abs(netProfit))}
            </span>
          </div>
        </div>
      </Card>
    </>
  );
};

export default IncomeStatement;
